import {
  SellPlan,
  SellSelectionTotals,
} from 'modules/incentivesMarket/pages/IncentiveOfferDetail/types/SellUnitsSelection';

export const planNet = (plan: SellPlan, units: number): number =>
  units * (plan.salePrice - plan.exercisePrice) * (1 - plan.retention / 100);

export const computeSellTotals = (
  plans: SellPlan[],
  selected: Record<string, number>
): SellSelectionTotals => {
  let units = 0;
  let gross = 0;
  let cost = 0;
  let retention = 0;
  let net = 0;

  plans.forEach((plan) => {
    const u = selected[plan.id] || 0;
    units += u;
    gross += u * plan.salePrice;
    cost += u * plan.exercisePrice;
    const spread = u * (plan.salePrice - plan.exercisePrice);
    retention += spread * (plan.retention / 100);
    net += planNet(plan, u);
  });

  return { units, gross, cost, retention, net };
};

export const computeTotalMaxNet = (plans: SellPlan[]): number =>
  plans.reduce((sum, plan) => sum + planNet(plan, plan.maxSellable), 0);

export const computeTotalMaxUnits = (plans: SellPlan[]): number =>
  plans.reduce((sum, plan) => sum + plan.maxSellable, 0);

export const buildEmptySelection = (
  plans: SellPlan[]
): Record<string, number> =>
  Object.fromEntries(plans.map((plan) => [plan.id, 0]));

export const buildSelectionByFactor = (
  plans: SellPlan[],
  factor: number
): Record<string, number> => {
  const next: Record<string, number> = {};
  plans.forEach((plan) => {
    next[plan.id] = Math.round(plan.maxSellable * factor);
  });
  return next;
};

export const clampPlanUnits = (
  planId: string,
  value: number,
  maxByPlan: Record<string, number>
): number => {
  const max = maxByPlan[planId] ?? 0;
  return Math.max(0, Math.min(max, Math.round(value || 0)));
};

export const distributeSellUnitsFifo = (
  plans: SellPlan[],
  totalUnits: number
): Record<string, number> => {
  let remaining = Math.max(0, Math.round(totalUnits));
  const next = buildEmptySelection(plans);

  plans.forEach((plan) => {
    const unitsSold = Math.min(plan.maxSellable, remaining);
    next[plan.id] = unitsSold;
    remaining -= unitsSold;
  });

  return next;
};

export const totalUnitsFromTargetAmount = ({
  plans,
  totalMaxNet,
  rawAmount,
}: {
  plans: SellPlan[];
  totalMaxNet: number;
  rawAmount: string;
}): number => {
  const target = Math.max(
    0,
    Number(String(rawAmount).replace(',', '.')) || 0
  );
  const totalMax = computeTotalMaxUnits(plans);
  const frac = totalMaxNet ? Math.min(1, target / totalMaxNet) : 0;
  return Math.round(totalMax * frac);
};

export const applyAmountDistribution = ({
  plans,
  totalMaxNet,
  rawAmount,
}: {
  plans: SellPlan[];
  totalMaxNet: number;
  rawAmount: string;
}): Record<string, number> =>
  distributeSellUnitsFifo(
    plans,
    totalUnitsFromTargetAmount({ plans, totalMaxNet, rawAmount })
  );
